/**
 * DrawingCard.js — Fix Round v7 (2026-07-12) · Complete rebuild.
 *
 * THE ONLY component that renders a drawing preview anywhere in the app.
 * Replaces DrawingThumbnail and all inline `<img src={thumbnail_url}>`
 * markup on every surface: per-project Plan Room register, issue sets,
 * transmittal contents, My Desk company-wide Plan Room, unfiled intake,
 * Contractor Portal Plans tab, gallery grids.
 *
 * Principle (from the user's directive): "A THUMBNAIL IS NOT A THING."
 * There is ONE file identity — `drawing.current_file_url` — and the card
 * preview is just a small client-side render of that same file, fetched
 * over the exact same authed path the full viewer already uses.
 *
 * No server thumbnail_url ever read. No pattern-guessed filenames. No
 * repair fallbacks. If `current_file_url` is empty → red "No file"
 * badge (the ONLY case that shows the red badge). Otherwise fetch via
 * `fetchDrawingFileBlob(drawing)` (same call site the viewer uses),
 * render page 1 with pdf.js for PDFs or <img> for raster images,
 * cache the result in IndexedDB keyed by `drawing.id + current_file_url`.
 *
 * Failure is never silent. Super-admin sees on-card diagnostics: the
 * exact URL attempted, the HTTP status, whether auth was present.
 */
import { useState, useEffect, useRef } from 'react';
import { FileText } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { pdfjs } from 'react-pdf';
import { fetchDrawingFileBlob } from '@/utils/authedFile';
import '@/utils/pdfWorker'; // side-effect: sets workerSrc + runs self-test

const DB_NAME = 'keymade_drawing_previews';
const STORE = 'previews';
const DB_VERSION = 1;
const MEM_CACHE = new Map();
const INFLIGHT = new Map();
const LAST_ERROR = new Map();

function _openDb() {
  return new Promise((resolve, reject) => {
    const r = indexedDB.open(DB_NAME, DB_VERSION);
    r.onupgradeneeded = () => {
      const db = r.result;
      if (!db.objectStoreNames.contains(STORE)) db.createObjectStore(STORE);
    };
    r.onsuccess = () => resolve(r.result);
    r.onerror = () => reject(r.error);
  });
}

async function _idbGet(key) {
  try {
    const db = await _openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readonly');
      const req = tx.objectStore(STORE).get(key);
      req.onsuccess = () => resolve(req.result || null);
      req.onerror = () => resolve(null);
    });
  } catch { return null; }
}

async function _idbSet(key, blob) {
  try {
    const db = await _openDb();
    return await new Promise((resolve) => {
      const tx = db.transaction(STORE, 'readwrite');
      tx.objectStore(STORE).put(blob, key);
      tx.oncomplete = () => resolve(true);
      tx.onerror = () => resolve(false);
    });
  } catch { return false; }
}

async function _renderPdfPageOneToJpeg(pdfBlob, maxWidth = 512) {
  const buf = await pdfBlob.arrayBuffer();
  const pdf = await pdfjs.getDocument({ data: buf }).promise;
  const page = await pdf.getPage(1);
  const baseVp = page.getViewport({ scale: 1 });
  const scale = Math.min(2, maxWidth / baseVp.width);
  const vp = page.getViewport({ scale });
  const canvas = document.createElement('canvas');
  canvas.width = Math.ceil(vp.width);
  canvas.height = Math.ceil(vp.height);
  await page.render({ canvasContext: canvas.getContext('2d'), viewport: vp }).promise;
  const out = await new Promise((r) => canvas.toBlob(r, 'image/jpeg', 0.82));
  try { pdf.destroy(); } catch { /* best-effort */ }
  return out;
}

async function _renderImageToJpeg(srcBlob, maxWidth = 512) {
  const url = URL.createObjectURL(srcBlob);
  try {
    const img = new Image();
    await new Promise((res, rej) => { img.onload = res; img.onerror = rej; img.src = url; });
    const scale = Math.min(1, maxWidth / img.width);
    const c = document.createElement('canvas');
    c.width = Math.ceil(img.width * scale);
    c.height = Math.ceil(img.height * scale);
    c.getContext('2d').drawImage(img, 0, 0, c.width, c.height);
    return await new Promise((r) => c.toBlob(r, 'image/jpeg', 0.82));
  } finally { URL.revokeObjectURL(url); }
}

async function _buildPreviewBlob(drawing) {
  // fetchDrawingFileBlob reads EXACTLY drawing.current_file_url — same
  // field the viewer reads. Returns {blob, url, status, hadToken, isBackend, error}.
  const res = await fetchDrawingFileBlob(drawing);
  if (!res.blob) {
    LAST_ERROR.set(drawing.id, {
      name: 'FetchError',
      message: res.error || 'no blob returned',
      url: res.url, status: res.status,
      hadToken: !!res.hadToken, isBackend: !!res.isBackend,
    });
    return null;
  }
  const head = new Uint8Array(await res.blob.slice(0, 4).arrayBuffer());
  const isPdf = head[0] === 0x25 && head[1] === 0x50 && head[2] === 0x44 && head[3] === 0x46;
  try {
    if (isPdf) return await _renderPdfPageOneToJpeg(res.blob, 512);
    if (res.blob.type.startsWith('image/')) return await _renderImageToJpeg(res.blob, 512);
    LAST_ERROR.set(drawing.id, {
      name: 'UnsupportedContent',
      message: `not a PDF or image (blob.type=${res.blob.type || 'unknown'})`,
      url: res.url, status: res.status,
      hadToken: !!res.hadToken, isBackend: !!res.isBackend,
    });
    return null;
  } catch (err) {
    LAST_ERROR.set(drawing.id, {
      name: err?.name || 'RenderError',
      message: err?.message || String(err),
      url: res.url, status: res.status,
      hadToken: !!res.hadToken, isBackend: !!res.isBackend,
    });
    throw err;
  }
}

async function _getPreviewUrl(drawing) {
  if (!drawing?.id || !drawing?.current_file_url) return null;
  const key = `${drawing.id}::${drawing.current_file_url}`;
  if (MEM_CACHE.has(key)) return MEM_CACHE.get(key);
  if (INFLIGHT.has(key)) return INFLIGHT.get(key);

  const p = (async () => {
    const cached = await _idbGet(key);
    if (cached instanceof Blob) {
      const u = URL.createObjectURL(cached);
      MEM_CACHE.set(key, u);
      LAST_ERROR.delete(drawing.id);
      return u;
    }
    const blob = await _buildPreviewBlob(drawing);
    if (!blob) return null;
    try { await _idbSet(key, blob); } catch { /* IDB write best-effort */ }
    const u = URL.createObjectURL(blob);
    MEM_CACHE.set(key, u);
    LAST_ERROR.delete(drawing.id);
    return u;
  })().finally(() => INFLIGHT.delete(key));

  INFLIGHT.set(key, p);
  return p;
}

export default function DrawingCard({
  drawing,
  isSuperAdmin = false,
  className = 'w-full h-full object-cover',
  fallbackClassName = '',
  fallbackIconClassName = 'w-12 h-12 text-gray-300',
  testId,
  // DR-7 — a client is not shown "Preview failed"; they are told who to tell.
  // The firm's own pages pass nothing and keep the badge they had.
  errorLabel,
  errorAction,
}) {
  const [src, setSrc] = useState(null);
  const [state, setState] = useState('loading'); // loading | ok | no-file | error
  const [errInfo, setErrInfo] = useState(null);
  const cancelRef = useRef(false);

  useEffect(() => {
    cancelRef.current = false;
    setErrInfo(null);
    setSrc(null);
    if (!drawing?.current_file_url) {
      setState('no-file');
      return () => { cancelRef.current = true; };
    }
    setState('loading');
    (async () => {
      try {
        const url = await _getPreviewUrl(drawing);
        if (cancelRef.current) return;
        if (url) {
          setSrc(url);
          setState('ok');
        } else {
          setState('error');
          setErrInfo(LAST_ERROR.get(drawing.id) || null);
        }
      } catch (err) {
        if (cancelRef.current) return;
        setState('error');
        setErrInfo(LAST_ERROR.get(drawing.id) || {
          name: err?.name || 'Error',
          message: err?.message || String(err),
        });
      }
    })();
    return () => { cancelRef.current = true; };
  }, [drawing?.id, drawing?.current_file_url]);

  if (state === 'no-file') {
    return (
      <>
        <div
          className={`w-full h-full flex items-center justify-center ${fallbackClassName}`}
          data-testid={testId ? `${testId}-nofile` : 'drawing-card-nofile'}
          data-card-state="no-file"
        >
          <FileText className={fallbackIconClassName} />
        </div>
        <div className="absolute inset-0 flex items-center justify-center bg-red-500/10 backdrop-blur-[1px] pointer-events-none">
          <Badge className="bg-red-600 text-white text-[10px] font-semibold shadow-lg">
            No file — upload v1
          </Badge>
        </div>
      </>
    );
  }

  if (src) {
    return (
      <img
        src={src}
        alt={drawing?.title || drawing?.drawing_number || 'drawing'}
        className={className}
        data-testid={testId}
        data-card-state="ok"
      />
    );
  }

  // Loading OR error fallback
  return (
    <>
      <div
        className={`w-full h-full flex items-center justify-center ${fallbackClassName}`}
        data-testid={testId ? `${testId}-fallback` : 'drawing-card-fallback'}
        data-card-state={state}
      >
        <FileText className={fallbackIconClassName} />
      </div>
      {state === 'error' && (
        <div
          className="absolute inset-0 flex items-center justify-center bg-red-500/10 backdrop-blur-[1px] pointer-events-none"
          data-testid={testId ? `${testId}-error` : 'drawing-card-error'}
        >
          {errorAction && (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); errorAction.onClick(); }}
              className="absolute bottom-2 text-[10px] underline text-white pointer-events-auto"
              data-testid={testId ? `${testId}-error-say` : 'drawing-card-error-say'}
            >
              {errorAction.label}
            </button>
          )}
          <Badge className="bg-orange-600 text-white text-[10px] font-semibold shadow-lg">
            {errorLabel || 'Preview failed'}
          </Badge>
        </div>
      )}
      {state === 'error' && isSuperAdmin && errInfo && (
        <div
          className="absolute bottom-0 left-0 right-0 p-1 text-[9px] font-mono leading-tight bg-black/85 text-red-300 pointer-events-none max-h-[75%] overflow-auto z-20"
          data-testid={testId ? `${testId}-diagnostic` : 'drawing-card-diagnostic'}
          data-err-url={errInfo.url || ''}
          data-err-status={errInfo.status ?? ''}
          data-err-had-token={String(!!errInfo.hadToken)}
        >
          <div><b>{errInfo.name}:</b> {errInfo.message}</div>
          {errInfo.url && <div className="opacity-90 break-all">url: {errInfo.url}</div>}
          <div className="opacity-90">
            status: <b>{errInfo.status ?? '—'}</b>
            {' · '}auth: <b>{errInfo.hadToken ? 'yes' : 'no'}</b>
          </div>
        </div>
      )}
    </>
  );
}
